import { firstValueFrom } from 'rxjs';

import { LoggerService } from '@common/modules/core/services/logger/logger.service';

import { ApiService } from '../api/services/api.service';
import { DEFAULT_RESOURCE_IDENTIFIER, DEFAULT_RESOURCE_TYPE, IArcMetric, IUserMetricDimensions, MICROSOFT_RESOURCE_ID_KEY } from './interfaces';

export class ArcMetricSubmitter {
  private queue: Array<IArcMetric> = [];

  private flushIntervalMs = 60 * 1000;
  private timer?: ReturnType<typeof setInterval>;
  private flushing = false;

  public constructor(private extensionId: string, private apiService: ApiService, private loggerService: LoggerService) {
    this.startTimer();
  }

  public trackMetric(name: string, value: number, dimensions: IUserMetricDimensions): void {
    const metricEntry = {
      name,
      value,
      dimensions: {
        // Default values for required dimensions; caller can override these
        ResourceType: DEFAULT_RESOURCE_TYPE,
        ResourceIdentifier: DEFAULT_RESOURCE_IDENTIFIER,
        ...dimensions,
        [MICROSOFT_RESOURCE_ID_KEY]: this.extensionId
      }
    } as IArcMetric;

    this.queue.push(metricEntry);
  }

  public destroy(): void {
    this.stopTimer();
    while (this.queue.length > 0) {
      this.flush().catch(() => {});
    }
  }

  private startTimer() {
    this.stopTimer();
    this.timer = setInterval(() => {
      this.flush();
    }, this.flushIntervalMs);
  }

  private async flush(): Promise<void> {
    if (this.flushing) {
      return;
    }
    if (this.queue.length === 0) {
      return;
    }

    this.flushing = true;
    try {
      const metricToSend = this.queue.slice(0, 100);
      this.queue = this.queue.slice(metricToSend.length);

      await firstValueFrom(this.apiService.EdgeExtensions.postMetrics(metricToSend));
    } catch (error) {
      this.loggerService.error('ArcMetricSubmitter: Failed to flush metrics', error);
    } finally {
      this.flushing = false;
    }
  }

  private stopTimer() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = undefined;
    }
  }
}
